// Lib
import { get, isNil } from 'lodash/fp';
import { assign, chunk, difference, isEmpty, keys } from 'lodash';

// Utils
import http from '../utils/services/http';
import { getSessionId } from '../device/deviceSessionService';

// Actions
import { loadUsers, userActivityLoad, userActivityError, loadUser } from './userActions';

// Utils
import { isGuestSelector } from './currentUserSelector';
import { isRealUserId } from '../../common/users/userHelper';
import { asyncResource } from '../utils/services/http/asyncResource/asyncResource';
import { getDataExpiryTimestamp } from '../utils/services/http/axiosResponseUtils';

// Selectors
import { getIdleOnlineUserIds, getTrackedUserIds } from '../utils/collaboration/remoteUsersSelector';

// Constants
import { METHODS } from '../../common/utils/http/httpConstants';
import { TIMES } from '../../common/utils/timeUtil';
import { ResourceTypes } from '../utils/services/http/asyncResource/asyncResourceConstants';

export const fetchMe = () => http({ url: 'users/me', timeout: 15000, retry: 1 });

export const fetchCurrentUserAppInitialisationData = ({ timezone, disableCache } = {}) => {
    const paramsObj = {};

    if (disableCache) {
        paramsObj.disable_cache = true;
    }

    if (!isNil(timezone)) {
        paramsObj.timezone = timezone;
    }

    const params = !isEmpty(paramsObj) ? paramsObj : undefined;
    return http({ url: 'users/me/app-init', timeout: 2 * TIMES.MINUTE, retry: 1, params });
};

export const fetchCurrentUserInitialQuickSearchBoardSummariesHttp = () =>
    http({ url: 'users/me/quick-search-init', timeout: 2 * TIMES.MINUTE, retry: 1 });

export const fetchCurrentUserInitialCustomTemplatesHttp = () =>
    http({ url: 'users/me/custom-templates-init', timeout: 2 * TIMES.MINUTE, retry: 1 });

export const updateMe = ({ userChanges }) =>
    http({ url: 'users/me', method: METHODS.PUT, timeout: 11000, retry: 1, data: { userChanges } }).then(
        (response) => response.data,
    );

export const updateCurrentUserEmail = ({ email, password }) =>
    http({ url: 'users/me/email', method: METHODS.PUT, timeout: 11000, retry: 1, data: { email, password } }).then(
        (response) => response.data,
    );

export const refreshMe = () => http({ url: 'users/me/refresh', timeout: 11000 });

/**
 * Retrieves just the user activity details for the specified user IDs.
 */
export const fetchUserActivity = (userIds) => (dispatch, getState) => {
    const state = getState();

    const isGuest = isGuestSelector(state);
    if (isGuest) return;

    const userIdsToFetch = userIds.filter(isRealUserId);

    if (!userIdsToFetch.length) return Promise.resolve();

    const userIdBatches = chunk(userIdsToFetch, 100);

    const currentSessionId = getSessionId();

    return Promise.all(
        userIdBatches.map((ids) => {
            const usersActivityHttpConf = {
                url: 'users/activity',
                params: {
                    ids: ids.join(','),
                },
            };

            return http(usersActivityHttpConf);
        }),
    )
        .then((responses) => {
            const usersMap = {};

            responses.forEach((response) => {
                const users = get(['data', 'users'], response) || {};

                for (const userEntryId in users) {
                    if (!users.hasOwnProperty(userEntryId)) continue;

                    const userEntry = users[userEntryId] || {};

                    if (userEntry.selectedElementsSessionId !== currentSessionId) {
                        usersMap[userEntryId] = userEntry;
                    }
                }
            });

            const disconnectedUserIds = difference(userIdsToFetch, keys(usersMap));
            dispatch(userActivityLoad({ users: usersMap, disconnectedUserIds }));
            return responses;
        })
        .catch((err) => {
            dispatch(userActivityError(userIdsToFetch, err));
            return Promise.reject(err);
        });
};

/**
 * Fetches the activity for all users that are known/connected to the current user.
 */
export const fetchAllUserActivity = () => (dispatch, getState) => {
    const state = getState();

    const userIds = getTrackedUserIds(state);

    return dispatch(fetchUserActivity(userIds));
};

/**
 * Checks for all idle users and retrieves their updated activity information if any are found.
 */
export const checkIdleUsers = () => (dispatch, getState) => {
    const state = getState();
    const idleUserIds = getIdleOnlineUserIds(state);

    if (!idleUserIds.length) return;

    return dispatch(fetchUserActivity(idleUserIds));
};

/**
 * Retrieves the user details as well as the user activity for the specified user IDs.
 */
export const fetchUsers =
    (userIds, force = false) =>
    (dispatch, getState) => {
        const realUserIds = userIds.filter(isRealUserId);

        if (isEmpty(realUserIds)) return;

        dispatch(
            asyncResource(
                ResourceTypes.users,
                realUserIds,
                force,
            )(async (missingUserIds) => {
                const userIdBatches = chunk(missingUserIds, 100);

                const responses = await Promise.all(
                    userIdBatches.map((ids) => {
                        dispatch(fetchUserActivity(ids));

                        return http({
                            url: 'users',
                            params: {
                                ids: ids.join(','),
                            },
                            retry: 3,
                        });
                    }),
                );

                const expiry = responses.reduce((minExpiry, response) => {
                    const responseExpiry = getDataExpiryTimestamp(response);

                    if (!minExpiry) return responseExpiry;

                    return Math.min(minExpiry, responseExpiry);
                }, undefined);

                let usersMap = {};

                responses.forEach((response) => {
                    const users = get(['data', 'users'], response) || {};
                    usersMap = assign(usersMap, users);
                });

                dispatch(loadUsers(usersMap));

                return {
                    responses,
                    asyncResource: {
                        expiry,
                    },
                };
            }),
        );
    };

let usersToFetch = [];
let fetchUsersTimeout;
export const debouncedBatchFetchUsers =
    (userIds = []) =>
    (dispatch) => {
        usersToFetch = usersToFetch.concat(userIds);

        if (fetchUsersTimeout) clearTimeout(fetchUsersTimeout);

        fetchUsersTimeout = setTimeout(() => {
            dispatch(fetchUsers(usersToFetch));
            usersToFetch = [];
        }, 100);
    };

// FIXME This doesn't use the resources
export const fetchUserWithEmail =
    ({ userEmail }) =>
    (dispatch, getState) => {
        if (!userEmail) {
            return Promise.reject(new Error('Must provide a user email.'));
        }

        const lowerCaseUserEmail = userEmail.toLowerCase();
        const state = getState();
        const users = state.get('users');

        const existingUser = users.find((userEntry) => userEntry.get('email') === lowerCaseUserEmail);

        if (existingUser) return Promise.resolve(existingUser.toJS());

        const userReq = {
            method: METHODS.GET,
            url: 'users',
            params: {
                email: lowerCaseUserEmail,
            },
        };

        return http(userReq).then((response) => {
            const userData = response.data.user;
            if (!userData || !userData._id) return;

            dispatch(loadUser(userData));

            return userData;
        });
    };

export const getOrInviteUser =
    ({ userEmail, initialEducation, onboarding }) =>
    (dispatch, getState) => {
        if (!userEmail) {
            return Promise.reject(new Error('Must provide a user email.'));
        }

        const lowerCaseUserEmail = userEmail.toLowerCase();
        const state = getState();
        const users = state.get('users');

        const filteredUsers = users.filter((userEntry) => userEntry.get('email') === lowerCaseUserEmail);
        const user = filteredUsers.size ? filteredUsers.first() : null;

        if (user) return Promise.resolve(user.toJS());

        const userReq = {
            method: METHODS.POST,
            url: 'users/get-or-invite',
            data: {
                email: lowerCaseUserEmail,
                initialEducation,
                onboarding,
            },
            retry: 3,
        };

        return http(userReq).then((response) => {
            const userData = response.data.user;
            dispatch(loadUser(userData));
            return userData;
        });
    };
